/**
 * Wall thickness edits (PRP-SKETCH-FIELD-2 E1-E5, G2, G3) and the
 * check-dimensions note (E4).
 *
 * Geometry is stored on the interior face (E1): a thicker wall pushes its outer
 * face out and never moves an interior face. A shared wall keeps both rooms'
 * interior sizes by moving the newer side; a side that cannot move gets a note.
 */
import { POLICY, inToFt } from './policy';
import { EPS, dist, edgeOf, findRoom, outN, tidy, unit } from './geom';
import { sharedMap } from './shared';
import { polysOverlap } from './overlap';
import { setWallIn, wallFtOf, wallIn } from './thickness';
import type { Fail, Measurement, Plan, Pt, Room, SharedMap, WallRef } from './types';

const nowIso = () => new Date().toISOString();
/** Inches for a note: 4.5 -> 4.5, 6 -> 6. */
const fmtIn = (n: number) => `${Number(n.toFixed(2))}"`;

/** E4: put the check-dimensions note on a room (the latest reason wins). */
export function noteCheckDims(r: Room, reason: string, now?: string): void {
  r.checkDims = { reason, at: now ?? nowIso() };
}

/** E4: Dismiss. */
export function dismissCheckDims(plan: Plan, rid: string): { ok: true } | Fail<'missing' | 'none'> {
  const r = findRoom(plan, rid);
  if (!r) return { ok: false, err: 'missing', message: 'That room is gone' };
  if (!r.checkDims) return { ok: false, err: 'none', message: 'Nothing to dismiss' };
  delete r.checkDims;
  return { ok: true };
}

/** The outline of a wall's thickness band: interior face to outer face. */
export function wallBand(r: Room, vid: number, thickFt: number): Pt[] | null {
  const E = edgeOf(r, vid);
  if (!E || dist(E.a, E.b) < EPS || thickFt < EPS) return null;
  const n = outN(r, E.a, E.b);
  return [E.a, E.b, { x: E.b.x + n.x * thickFt, y: E.b.y + n.y * thickFt }, { x: E.a.x + n.x * thickFt, y: E.a.y + n.y * thickFt }];
}

/** Rooms joined through shared walls, starting from `from`, never crossing a wall in `cut`. */
function joined(M: SharedMap, from: string[], cut: Set<string>): Set<string> {
  const seen = new Set(from), todo = [...from];
  while (todo.length) {
    const rid = todo.pop()!;
    M.get(rid)?.forEach((ivs, vid) => ivs.forEach((iv) => {
      if (cut.has(`${rid}:${vid}`) || cut.has(`${iv.orid}:${iv.ovid}`) || seen.has(iv.orid)) return;
      seen.add(iv.orid);
      todo.push(iv.orid);
    }));
  }
  return seen;
}

export interface ThicknessResult {
  ok: true;
  /** Every wall whose stored thickness changed (the selection and the walls across them). */
  changed: WallRef[];
  /** Rooms moved to keep interior sizes (E3). */
  moved: string[];
  /** Rooms that got the check-dimensions note (E2, E3). */
  checkDims: string[];
}

/** Two moves of one group agree when they differ by no more than corner rounding on both faces. */
const MOVE_AGREE_FT = 2 * EPS;

/** Every measurement and span in the plan, keyed, with its current value (feet). */
function measuredValues(plan: Plan): Map<string, { rid: string; ft: number }> {
  const out = new Map<string, { rid: string; ft: number }>();
  for (const r of plan.rooms) {
    // A span's far corner is in another room; corner ids are unique in the plan.
    const at = (id: number) => r.corners.find((c) => c.id === id)
      ?? plan.rooms.find((q) => q !== r && q.corners.some((c) => c.id === id))?.corners.find((c) => c.id === id);
    const add = (m: Measurement, key: string) => {
      const a = at(m.a), b = at(m.b);
      if (a && b) out.set(key, { rid: r.id, ft: Math.abs(b[m.axis] - a[m.axis]) });
    };
    r.measurements.forEach((m, i) => add(m, `${r.id}:m${i}`));
    r.spans?.forEach((m, i) => add(m, `${r.id}:s${i}`));
  }
  return out;
}

/** Rooms joined to each other that move together; `seeds` are the rooms facing the wall, each with its move. */
interface Mover { seeds: { id: string; d: number }[]; group: Set<string> }

/**
 * Each room's move (feet along `dir`) in a joined group (W3): a seed moves by its
 * own gap's difference and a joined room moves with the room it is joined to. Two
 * rooms may move by different amounts only across a wall parallel to `dir` (the
 * wall slides along itself and keeps its thickness). Null when no set of moves
 * keeps every other wall in the group at its gap.
 */
function groupMoves(plan: Plan, M: SharedMap, m: Mover, cut: Set<string>, dir: Pt): Map<string, number> | null {
  const d = new Map<string, number>();
  for (const s of m.seeds) {
    const had = d.get(s.id);
    if (had != null && Math.abs(had - s.d) > MOVE_AGREE_FT) return null;
    d.set(s.id, s.d);
  }
  const slides = (rid: string, vid: number) => {
    const E = edgeOf(findRoom(plan, rid)!, vid)!, t = unit(E.a, E.b);
    return Math.abs(t.x * dir.y - t.y * dir.x) < EPS;
  };
  const links = (rid: string) => [...(M.get(rid) ?? [])].flatMap(([vid, ivs]) => ivs
    .filter((iv) => m.group.has(iv.orid) && !cut.has(`${rid}:${vid}`) && !cut.has(`${iv.orid}:${iv.ovid}`))
    .map((iv) => ({ to: iv.orid, slides: slides(rid, vid) })));
  // Rigid joins first, so a room takes the move of the room it cannot slide past.
  for (const rigidOnly of [true, false]) {
    const todo = [...d.keys()];
    while (todo.length) {
      const id = todo.pop()!;
      for (const l of links(id)) {
        if ((rigidOnly && l.slides) || d.has(l.to)) continue;
        d.set(l.to, d.get(id)!);
        todo.push(l.to);
      }
    }
  }
  for (const id of m.group) {
    for (const l of links(id)) if (!l.slides && Math.abs(d.get(id)! - d.get(l.to)!) > MOVE_AGREE_FT) return null;
  }
  return d;
}

/**
 * Set the thickness of `walls` (E1-E3). A wall with no room behind it changes
 * nothing else; a room its new outer face would overlap gets the note (E2). A
 * shared wall takes the thickness on both rooms, and the newer side (the side
 * holding the room added last) moves with every room joined to it on that side.
 * Each room across moves by its OWN gap's difference, so every face ends at the
 * new thickness. A side also held on the other side, or a group that would need
 * two different moves, does not move: the wall shows its gap or overlap mark and
 * the newer room gets the note (E3). Any measurement (or split span) whose value
 * the move changes puts the note on its room, naming the wall (E4; `why` ends the
 * reason, e.g. "for the island"). One undo step (`History.run`).
 */
export function setWallThickness(
  plan: Plan, walls: WallRef[], thicknessIn: number, opts: { now?: string; why?: string } = {},
): ThicknessResult | Fail<'missing' | 'invalid' | 'no-change'> {
  if (!Number.isFinite(thicknessIn) || thicknessIn < 0) return { ok: false, err: 'invalid', message: 'Enter a thickness' };
  for (const w of walls) {
    const r = findRoom(plan, w.rid);
    if (!r || !edgeOf(r, w.vid)) return { ok: false, err: 'missing', message: 'That wall is gone' };
  }
  const now = opts.now ?? nowIso();
  const res: ThicknessResult = { ok: true, changed: [], moved: [], checkDims: [] };
  const noted = new Set<string>();
  const note = (r: Room, reason: string) => { noteCheckDims(r, reason, now); noted.add(r.id); };
  const idx = (rid: string) => plan.rooms.findIndex((q) => q.id === rid);
  for (const w of walls) {
    const r = findRoom(plan, w.rid)!;
    const oldIn = wallIn(r, w.vid);
    if (oldIn === thicknessIn) continue;
    const M = sharedMap(plan);
    const ivs = M.get(r.id)?.get(w.vid) ?? [];
    const E = edgeOf(r, w.vid)!;
    const n = outN(r, E.a, E.b);
    setWallIn(r, w.vid, thicknessIn);
    res.changed.push({ rid: r.id, vid: w.vid });
    const newFt = inToFt(thicknessIn);
    if (!ivs.length) {
      // E2: only what the new outer face now covers is affected.
      const band = wallBand(r, w.vid, newFt);
      for (const q of plan.rooms) {
        if (q === r || !band || !polysOverlap(band, q.corners)) continue;
        note(q, `${r.name} wall changed from ${fmtIn(oldIn)} to ${fmtIn(thicknessIn)} and now overlaps this room`);
      }
      continue;
    }
    const cut = new Set([`${r.id}:${w.vid}`]);
    const across = new Set<string>();
    const oldOf = new Map<string, number>([[r.id, oldIn]]);
    for (const iv of ivs) {
      const q = findRoom(plan, iv.orid)!;
      if (!oldOf.has(q.id)) oldOf.set(q.id, wallIn(q, iv.ovid));
      setWallIn(q, iv.ovid, thicknessIn);
      res.changed.push({ rid: q.id, vid: iv.ovid });
      cut.add(`${q.id}:${iv.ovid}`);
      across.add(q.id);
    }
    const newest = Math.max(idx(r.id), ...[...across].map(idx));
    const moveAcross = newest !== idx(r.id);
    const other = moveAcross ? new Set([r.id]) : across;
    const names = [...across].map((id) => findRoom(plan, id)!.name).join(', ');
    // The moves: one per room across, each by its own gap (W3), merged where the
    // groups join; or the selected room alone, which must close every gap at once.
    const movers: Mover[] = [];
    const seeds = ivs.map((iv) => ({ id: moveAcross ? iv.orid : r.id, d: newFt - iv.gapFt }));
    for (const s of seeds) {
      const merged: Mover = { seeds: [s], group: joined(M, [s.id], cut) };
      for (const m of movers.filter((q) => [...merged.group].some((id) => q.group.has(id)))) {
        merged.seeds.push(...m.seeds);
        m.group.forEach((id) => merged.group.add(id));
        movers.splice(movers.indexOf(m), 1);
      }
      movers.push(merged);
    }
    const dir = moveAcross ? n : { x: -n.x, y: -n.y };
    const before = measuredValues(plan);
    const movedNow = new Set<string>();
    for (const m of movers) {
      const moves = [...m.group].some((id) => other.has(id)) ? null : groupMoves(plan, M, m, cut, dir);
      if (!moves) {
        // Held on both sides, or no set of moves keeps every gap: it stays (E3).
        const newer = plan.rooms[Math.max(...m.seeds.map((s) => idx(s.id)))];
        const to = newer === r ? names : r.name;
        note(newer, `Wall to ${to} changed from ${fmtIn(oldOf.get(newer.id) ?? oldIn)} to ${fmtIn(thicknessIn)}`);
        continue;
      }
      moves.forEach((delta, id) => {
        findRoom(plan, id)!.corners.forEach((p) => { p.x += dir.x * delta; p.y += dir.y * delta; });
        movedNow.add(id);
        if (!res.moved.includes(id)) res.moved.push(id);
      });
    }
    // A moved room that now overlaps a room it is not joined to gets the note.
    for (const id of movedNow) {
      const q = findRoom(plan, id)!;
      for (const s of plan.rooms) {
        if (movedNow.has(s.id) || !polysOverlap(q.corners, s.corners)) continue;
        note(q, `Moved for the wall to ${moveAcross ? r.name : names} and now overlaps ${s.name}`);
      }
    }
    // A measurement the move changed is a size changed: its room gets the note (E4).
    const why = opts.why ? ` ${opts.why}` : '';
    measuredValues(plan).forEach((v, key) => {
      const was = before.get(key);
      if (!was || Math.abs(v.ft - was.ft) <= MOVE_AGREE_FT) return;
      const q = findRoom(plan, v.rid)!;
      const wall = q === r ? `Wall to ${names}` : across.has(q.id) ? `Wall to ${r.name}` : `Wall between ${r.name} and ${names}`;
      note(q, `${wall} set to ${fmtIn(thicknessIn)}${why}`);
    });
  }
  tidy(plan);
  res.checkDims = [...noted];
  if (!res.changed.length) return { ok: false, err: 'no-change', message: `Already ${fmtIn(thicknessIn)}` };
  return res;
}

/**
 * DD-SF2.10: a partial missing wall (any kind that keeps a wall, E13) never sits
 * in a 0 in wall. When shared wall `vid` of room `rid` stores 0 in, it is first
 * thickened to the plan's interior default through `setWallThickness` (E3: the
 * newer side moves, nothing is resized; a room that cannot move, or whose
 * measurement the move changes, gets the note naming the wall "for the `what`").
 * Every room the thickening moved also gets the note, "Moved 4" so the `what` has
 * a wall", unless this edit already noted it (that note is kept). Returns null
 * when the wall already has a thickness or is not shared.
 */
export function keepWallFor(
  plan: Plan, rid: string, vid: number, opts: { now?: string; what: string },
): ThicknessResult | null {
  const r = findRoom(plan, rid);
  if (!r || wallIn(r, vid) !== 0 || !(sharedMap(plan).get(rid)?.get(vid)?.length)) return null;
  const t = plan.defaults.interiorWallIn ?? POLICY.interior_wall_in;
  const now = opts.now ?? nowIso();
  const res = setWallThickness(plan, [{ rid, vid }], t, { now, why: `for the ${opts.what}` });
  if (!res.ok) return null;
  for (const id of res.moved) {
    if (res.checkDims.includes(id)) continue;
    noteCheckDims(findRoom(plan, id)!, `Moved ${fmtIn(t)} so the ${opts.what} has a wall`, now);
    res.checkDims.push(id);
  }
  return res;
}

/**
 * E5: the walls Set exterior walls opens with: every wall with no room behind it,
 * plus every wall carrying a window (THE_BIBLE §6.1a). Zero-length walls are left out.
 */
export function exteriorSeed(plan: Plan): WallRef[] {
  const M = sharedMap(plan);
  const out: WallRef[] = [];
  for (const r of plan.rooms) {
    const n = r.corners.length;
    r.corners.forEach((a, i) => {
      if (dist(a, r.corners[(i + 1) % n]) < POLICY.min_segment_ft) return;
      const behind = (M.get(r.id)?.get(a.id)?.length ?? 0) > 0;
      const win = r.openings.some((o) => o.edgeStart === a.id && o.kind === 'window');
      if (!behind || win) out.push({ rid: r.id, vid: a.id });
    });
  }
  return out;
}

/** Set exterior walls, Apply (G3): the selection at the chosen (or the plan's exterior default) thickness. */
export function applyExteriorWalls(
  plan: Plan, walls: WallRef[], thicknessIn?: number, opts: { now?: string } = {},
): ReturnType<typeof setWallThickness> {
  return setWallThickness(plan, walls, thicknessIn ?? plan.defaults.exteriorWallIn, opts);
}

/** Plan settings (G2, E5): thickness for new walls and the Set exterior walls default. */
export function setWallDefaults(
  plan: Plan, d: { interiorWallIn?: number; exteriorWallIn?: number },
): { ok: true } | Fail<'invalid'> {
  const bad = (v?: number) => v != null && !(Number.isFinite(v) && v > 0);
  if (bad(d.interiorWallIn) || bad(d.exteriorWallIn)) return { ok: false, err: 'invalid', message: 'Enter a thickness' };
  if (d.interiorWallIn != null) plan.defaults.interiorWallIn = d.interiorWallIn;
  if (d.exteriorWallIn != null) plan.defaults.exteriorWallIn = d.exteriorWallIn;
  return { ok: true };
}

/**
 * After a room is moved flush to another (F10 snap), the two sides of a new
 * shared wall can store different thicknesses. The side whose stored value does
 * not match the gap takes the other side's (the gap is what was drawn).
 */
export function reconcileShared(plan: Plan): number {
  let changed = 0;
  sharedMap(plan).forEach((m, rid) => m.forEach((ivs, vid) => ivs.forEach((iv) => {
    const r = findRoom(plan, rid)!, q = findRoom(plan, iv.orid)!;
    const mine = wallFtOf(r, vid), theirs = wallFtOf(q, iv.ovid);
    if (Math.abs(mine - theirs) < EPS || Math.abs(iv.gapFt - theirs) >= Math.abs(iv.gapFt - mine)) return;
    setWallIn(r, vid, wallIn(q, iv.ovid));
    changed++;
  })));
  return changed;
}
